/**
 * FeedbackDialog — Send Feedback modal wired with Formik, Yup validation,
 * and the submitFeedback thunk.
 *
 * Layout follows the v3 dialog system (DialogShell + Dialog) so the
 * surface, hairlines and footer-actions match every other modal in
 * the app.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import {
  Button,
  Dialog,
  SegmentedControl,
  Textarea,
  UnsavedChangesModal,
  styled,
} from '@shared/ui';
import {
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogFooterActions,
  DialogHeader,
  DialogSectionTitle,
  DialogTitle,
  DialogTitleBlock,
} from '@shared/ui/DialogShell';
import {
  enqueueSnackbar,
  getTransitionWithVariable,
  SNACKBAR_VARIANTS,
  submitFeedback,
} from 'redux_store';
import { buildFeedbackSchema, FEEDBACK_LIMITS } from '../model/feedbackSchema';
import { collectContext } from '../model/collectContext';
import { validateFiles, FILE_REJECT_REASONS } from '../model/validateFiles';
import { useReportUnsavedWork } from '@shared/hooks/useReportUnsavedWork';

const Body = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  padding: 20,
});

const Field = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const CounterRow = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  fontSize: 11,
  color: 'var(--qhs-text-3)',
  marginTop: -2,
});

const Dropzone = styled('label', {
  shouldForwardProp: p => p !== '$dragging',
})(({ $dragging }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '22px 18px',
  border: `1.5px dashed ${$dragging ? 'var(--qhs-brand)' : 'var(--qhs-border-strong)'}`,
  borderRadius: 10,
  background: $dragging ? 'var(--qhs-surface-hover)' : 'var(--qhs-bg)',
  color: 'var(--qhs-text-2)',
  cursor: 'pointer',
  transition: 'all 150ms',
  textAlign: 'center',
  '&:hover': {
    borderColor: 'var(--qhs-brand)',
    color: 'var(--qhs-brand)',
  },
}));

const DropzoneTitle = styled('span')({
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--qhs-text-1)',
});

const DropzoneHint = styled('span')({
  fontSize: 11,
  color: 'var(--qhs-text-3)',
});

const AttachmentList = styled('ul')({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const AttachmentRow = styled('li')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 10px',
  borderRadius: 8,
  background: 'var(--qhs-bg)',
  border: '1px solid var(--qhs-border)',
});

const Thumb = styled('div')({
  width: 36,
  height: 36,
  borderRadius: 6,
  background: 'var(--qhs-surface-hover)',
  color: 'var(--qhs-text-3)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  overflow: 'hidden',
  '& img': { width: '100%', height: '100%', objectFit: 'cover' },
});

const AttachmentMeta = styled('div')({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

const AttachmentName = styled('span')({
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--qhs-text-1)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const AttachmentSize = styled('span')({
  fontSize: 11,
  color: 'var(--qhs-text-3)',
});

const RemoveButton = styled('button')({
  background: 'transparent',
  border: 'none',
  color: 'var(--qhs-text-3)',
  cursor: 'pointer',
  padding: 4,
  borderRadius: 6,
  display: 'inline-flex',
  '&:hover': {
    background: 'var(--qhs-st-critical-bg)',
    color: 'var(--qhs-st-critical-tx)',
  },
});

const UploadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const PaperclipIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const formatBytes = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Hooks cannot be called inside a render prop, so the registry is fed
 * from a component the render prop mounts. The mandatory-update screen
 * asks that registry before it replaces the app; this dialog guards
 * itself and was otherwise invisible to it.
 */
const UnsavedWorkReporter = ({ isDirty }) => {
  useReportUnsavedWork(isDirty);
  return null;
};

const FeedbackDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { feedback, forms, notifications, btn, unsavedChanges } = useSelector(state => state.i18n);
  const currentAccountUuid = useSelector(state => state.user?.metadata?.currentAccount?.uuid);

  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [unsavedConfirmOpen, setUnsavedConfirmOpen] = useState(false);
  const fileInputRef = useRef(null);

  const schema = useMemo(() => buildFeedbackSchema(forms), [forms]);

  const categoryOptions = useMemo(
    () => [
      { value: 'improvement', label: feedback.categoryImprovement },
      { value: 'issue', label: feedback.categoryIssue },
      { value: 'other', label: feedback.categoryOther },
    ],
    [feedback]
  );

  const placeholders = useMemo(
    () => ({
      improvement: feedback.placeholderImprovement,
      issue: feedback.placeholderIssue,
      other: feedback.placeholderOther,
    }),
    [feedback]
  );

  const resetAll = useCallback(formikReset => {
    setFiles(prev => {
      prev.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
      return [];
    });
    setDragging(false);
    setUnsavedConfirmOpen(false);
    if (formikReset) formikReset();
  }, []);

  const isDirty = formikProps =>
    formikProps?.dirty || formikProps?.values?.message?.length > 0 || files.length > 0;

  const requestClose = formikProps => {
    if (formikProps?.isSubmitting) return;
    if (isDirty(formikProps)) {
      setUnsavedConfirmOpen(true);
      return;
    }
    resetAll(formikProps?.resetForm);
    onClose?.();
  };

  const confirmDiscard = formikProps => {
    resetAll(formikProps?.resetForm);
    onClose?.();
  };

  const addFiles = incoming => {
    const { accepted, rejected } = validateFiles({
      incoming: Array.from(incoming),
      existing: files,
      limits: FEEDBACK_LIMITS,
    });

    if (rejected.length > 0) {
      const messageFor = ({ file, reason }) => {
        const tpl =
          reason === FILE_REJECT_REASONS.MIME
            ? feedback.fileRejectedMime
            : reason === FILE_REJECT_REASONS.SIZE
            ? feedback.fileRejectedSize
            : reason === FILE_REJECT_REASONS.COUNT
            ? feedback.fileRejectedCount
            : feedback.fileRejectedTotalSize;
        return getTransitionWithVariable(tpl, { name: file.name });
      };
      dispatch(
        enqueueSnackbar({
          message: rejected.map(messageFor).join('\n'),
          options: { variant: SNACKBAR_VARIANTS.warning },
        })
      );
    }

    if (accepted.length > 0) {
      const next = accepted.map(file => ({
        id: `${file.name}-${file.size}-${file.lastModified}`,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      }));
      setFiles(prev => [...prev, ...next]);
    }
  };

  const handleFileInput = e => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = e => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  const handleDragOver = e => {
    e.preventDefault();
    if (!dragging) setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const removeFile = id => {
    setFiles(prev => {
      const target = prev.find(f => f.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter(f => f.id !== id);
    });
  };

  const handleSubmit = async (values, { setSubmitting, setErrors, resetForm }) => {
    const context = collectContext({
      win: window,
      dashboardVersion: process.env.REACT_APP_BUILD_NUMBER,
      now: () => new Date(),
    });

    const result = await dispatch(
      submitFeedback({
        currentAccountUuid,
        category: values.category,
        message: values.message.trim(),
        attachments: files.map(f => f.file),
        context,
      })
    );

    setSubmitting(false);

    if (submitFeedback.fulfilled.match(result)) {
      if (result.payload?.submissionId) {
        // eslint-disable-next-line no-console
        console.debug('[feedback] submitted', result.payload.submissionId);
      }
      dispatch(
        enqueueSnackbar({
          message: notifications.feedbackSent,
          options: { variant: SNACKBAR_VARIANTS.success },
        })
      );
      resetAll(resetForm);
      onClose?.();
      return;
    }

    const fieldErrors = result.payload?.validationErrors;
    if (fieldErrors && typeof fieldErrors === 'object') {
      setErrors(fieldErrors);
    }
    dispatch(
      enqueueSnackbar({
        message: result.payload?.error || notifications.feedbackFailed,
        options: { variant: SNACKBAR_VARIANTS.error },
      })
    );
  };

  return (
    <Formik
      initialValues={{ category: '', message: '' }}
      validationSchema={schema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      enableReinitialize={false}
    >
      {formikProps => {
        const { values, errors, touched, setFieldValue, handleChange, isSubmitting, submitForm } =
          formikProps;

        return (
          <>
            {/* Fed from in here: hooks cannot run inside a render prop. */}
            <UnsavedWorkReporter isDirty={isDirty(formikProps)} />
            <Dialog
              open={open}
              onClose={() => requestClose(formikProps)}
              ariaLabel={feedback.dialogTitle}
              maxWidth={560}
              header={
                <DialogHeader>
                  <DialogTitleBlock>
                    <DialogTitle>{feedback.dialogTitle}</DialogTitle>
                    <DialogDescription>{feedback.dialogSubtitle}</DialogDescription>
                  </DialogTitleBlock>
                </DialogHeader>
              }
              footer={
                <DialogFooter>
                  <DialogFooterActions>
                    <Button
                      variant="ghost"
                      onClick={() => requestClose(formikProps)}
                      disabled={isSubmitting}
                    >
                      {btn.cancel}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={submitForm}
                      disabled={
                        isSubmitting ||
                        !values.category ||
                        values.message.trim().length < FEEDBACK_LIMITS.messageMin
                      }
                    >
                      {isSubmitting ? feedback.sendingButton : feedback.sendButton}
                    </Button>
                  </DialogFooterActions>
                </DialogFooter>
              }
            >
              <DialogBody>
                <Body>
                  <Field>
                    <DialogSectionTitle>{feedback.categoryLabel}</DialogSectionTitle>
                    <SegmentedControl
                      fullWidth
                      value={values.category}
                      onChange={v => setFieldValue('category', v)}
                      options={categoryOptions}
                      error={!!(touched.category && errors.category)}
                    />
                  </Field>

                  <Field>
                    <DialogSectionTitle>{feedback.messageLabel}</DialogSectionTitle>
                    <Textarea
                      fullWidth
                      rows={5}
                      name="message"
                      value={values.message}
                      onChange={handleChange}
                      maxLength={FEEDBACK_LIMITS.messageMax}
                      placeholder={placeholders[values.category] || placeholders.other}
                      error={!!(touched.message && errors.message) || undefined}
                    />
                    <CounterRow>
                      {values.message.length} / {FEEDBACK_LIMITS.messageMax}
                    </CounterRow>
                  </Field>

                  <Field>
                    <DialogSectionTitle>{feedback.attachmentsLabel}</DialogSectionTitle>
                    <Dropzone
                      $dragging={dragging}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                    >
                      <UploadIcon />
                      <DropzoneTitle>{feedback.dropzoneTitle}</DropzoneTitle>
                      <DropzoneHint>{feedback.dropzoneHint}</DropzoneHint>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFileInput}
                      />
                    </Dropzone>

                    {files.length > 0 && (
                      <AttachmentList>
                        {files.map(f => (
                          <AttachmentRow key={f.id}>
                            <Thumb>
                              {f.preview ? <img src={f.preview} alt="" /> : <PaperclipIcon />}
                            </Thumb>
                            <AttachmentMeta>
                              <AttachmentName>{f.name}</AttachmentName>
                              <AttachmentSize>{formatBytes(f.size)}</AttachmentSize>
                            </AttachmentMeta>
                            <RemoveButton
                              type="button"
                              onClick={() => removeFile(f.id)}
                              title={feedback.removeAttachment}
                            >
                              <CloseIcon />
                            </RemoveButton>
                          </AttachmentRow>
                        ))}
                      </AttachmentList>
                    )}
                  </Field>
                </Body>
              </DialogBody>
            </Dialog>
            <UnsavedChangesModal
              open={unsavedConfirmOpen}
              title={unsavedChanges?.title}
              message={unsavedChanges?.defaultMessage}
              keepEditingLabel={unsavedChanges?.keepEditingButton}
              discardLabel={unsavedChanges?.discardButton}
              onKeepEditing={() => setUnsavedConfirmOpen(false)}
              onDiscard={() => confirmDiscard(formikProps)}
            />
          </>
        );
      }}
    </Formik>
  );
};

export default FeedbackDialog;
